import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  ArrowLeft, Plus, Check, Trash2, Star, ShoppingBag, 
  User, Calendar, DollarSign, Store, Search
} from 'lucide-react';
import { toast } from 'sonner';
import API from '@/utils/api';
import AddItemDialog from './AddItemDialog';

// Green theme tokens
const THEME = {
  primary: '#86EFAC',
  light: '#DCFCE7',
  lighter: '#F0FDF4',
  dark: '#166534',
  icon: '#22C55E',
  border: '#D1FAE5',
  surface: '#FAFDF7',
};

const ShoppingListView = ({ list, onBack, onUpdate }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showPurchased, setShowPurchased] = useState(false);

  useEffect(() => {
    fetchItems();
  }, [list.id]);

  const fetchItems = async () => {
    try {
      const response = await API.get(`/shopping/items?list_id=${list.id}`);
      setItems(response.data);
    } catch (error) {
      console.error('Failed to fetch items:', error);
      toast.error('Failed to load items');
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePurchased = async (item) => {
    try {
      if (item.status === 'purchased') {
        await API.put(`/shopping/items/${item.id}/unpurchase`);
      } else {
        await API.put(`/shopping/items/${item.id}/purchase`);
      }
      fetchItems();
      onUpdate();
    } catch (error) {
      toast.error('Failed to update item');
    }
  };

  const handleDelete = async (id) => {
    try {
      await API.delete(`/shopping/items/${id}`);
      toast.success('Item removed');
      fetchItems();
      onUpdate();
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  const handleSaveAsFavorite = async (item) => {
    try {
      await API.post(`/shopping/items/${item.id}/to-favorite`);
      toast.success('Saved to favorites!');
      fetchItems();
    } catch (error) {
      toast.error('Failed to save as favorite');
    }
  };

  const neededItems = items.filter(i => i.status === 'needed');
  const purchasedItems = items.filter(i => i.status === 'purchased');
  
  const filteredNeeded = searchQuery 
    ? neededItems.filter(i => i.name.toLowerCase().includes(searchQuery.toLowerCase()))
    : neededItems;
  
  const filteredPurchased = searchQuery
    ? purchasedItems.filter(i => i.name.toLowerCase().includes(searchQuery.toLowerCase()))
    : purchasedItems;

  const getCategoryColor = (category) => {
    const colors = {
      grocery: { bg: '#DCFCE7', text: '#166534' },
      household: { bg: '#DBEAFE', text: '#1E40AF' },
      clothing: { bg: '#EDE9FE', text: '#5B21B6' },
      gift: { bg: '#FCE7F3', text: '#9D174D' },
      electronics: { bg: '#FEF3C7', text: '#92400E' },
      health: { bg: '#FEE2E2', text: '#991B1B' },
      other: { bg: '#F3F4F6', text: '#4B5563' }
    };
    return colors[category] || colors.other;
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-12 w-full rounded-xl" />
        <Skeleton className="h-24 w-full rounded-xl" />
        <Skeleton className="h-24 w-full rounded-xl" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div className="flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={onBack}
            className="hover:bg-green-50"
            style={{ color: THEME.dark }}
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          <div>
            <h3 className="text-xl font-semibold text-gray-900">{list.name}</h3>
            <p className="text-sm text-gray-500 mt-0.5">
              {neededItems.length} needed, {purchasedItems.length} purchased
            </p>
          </div>
        </div>
        <Button
          onClick={() => setDialogOpen(true)}
          className="w-full sm:w-auto shadow-sm hover:shadow-md transition-shadow"
          style={{ 
            backgroundColor: THEME.primary,
            color: THEME.dark,
          }}
          data-testid="add-item-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Item
        </Button>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          placeholder="Search items..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-11 py-5 rounded-xl border-gray-200 focus:border-green-300 focus:ring-green-200"
        />
      </div>

      {/* Needed Items */}
      <div className="space-y-3 mb-8">
        {filteredNeeded.length === 0 && !searchQuery ? (
          <div 
            className="text-center py-12 rounded-2xl border-2 border-dashed"
            style={{ 
              backgroundColor: THEME.lighter,
              borderColor: THEME.border,
            }}
          >
            <div 
              className="w-14 h-14 mx-auto rounded-xl flex items-center justify-center mb-3"
              style={{ backgroundColor: THEME.light }}
            >
              <ShoppingBag className="h-7 w-7" style={{ color: THEME.dark }} />
            </div>
            <p className="text-gray-500">No items yet. Add your first item!</p>
          </div>
        ) : (
          filteredNeeded.map((item) => {
            const categoryColor = getCategoryColor(item.category);
            return (
              <Card 
                key={item.id} 
                className="overflow-hidden transition-shadow hover:shadow-md"
                style={{ 
                  borderColor: THEME.border,
                  borderWidth: '1px',
                  borderLeftWidth: '4px',
                  borderLeftColor: THEME.primary,
                }}
              >
                <CardContent className="p-4 sm:p-5">
                  <div className="flex items-start gap-4">
                    <Checkbox
                      checked={false}
                      onCheckedChange={() => handleTogglePurchased(item)}
                      className="mt-1 h-5 w-5 border-2 border-gray-300 data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600"
                      data-testid={`checkbox-${item.id}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-gray-900">{item.name}</span>
                        {item.quantity && (
                          <Badge variant="outline" className="text-xs font-medium">
                            x{item.quantity}
                          </Badge>
                        )}
                        {item.priority === 1 && (
                          <Badge 
                            className="text-xs"
                            style={{ backgroundColor: '#FEE2E2', color: '#991B1B' }}
                          >
                            High
                          </Badge>
                        )}
                        <Badge 
                          className="text-xs font-medium"
                          style={{ 
                            backgroundColor: categoryColor.bg, 
                            color: categoryColor.text 
                          }}
                        >
                          {item.category}
                        </Badge>
                      </div>
                      
                      {/* Context - "Why am I buying this?" */}
                      {item.why_buying && (
                        <p 
                          className="text-sm mt-2 px-3 py-2 rounded-lg"
                          style={{ 
                            backgroundColor: THEME.lighter,
                            color: THEME.dark,
                          }}
                        >
                          {item.why_buying}
                        </p>
                      )}
                      
                      <div className="flex flex-wrap gap-3 mt-3 text-xs text-gray-500">
                        {item.linked_person && (
                          <span className="flex items-center gap-1.5">
                            <User className="h-3.5 w-3.5" /> {item.linked_person}
                          </span>
                        )}
                        {item.store && (
                          <span className="flex items-center gap-1.5">
                            <Store className="h-3.5 w-3.5" /> {item.store}
                          </span>
                        )}
                        {item.budget && (
                          <span className="flex items-center gap-1.5">
                            <DollarSign className="h-3.5 w-3.5" /> ${item.budget}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex gap-1">
                      {!item.favorite_id && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleSaveAsFavorite(item)}
                          className="text-amber-500 hover:text-amber-600 hover:bg-amber-50"
                          title="Save as favorite"
                        >
                          <Star className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(item.id)}
                        className="text-rose-400 hover:text-rose-600 hover:bg-rose-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Purchased Items (Collapsed) */}
      {filteredPurchased.length > 0 && (
        <div className="mt-8">
          <button
            onClick={() => setShowPurchased(!showPurchased)}
            className="flex items-center gap-2 text-sm font-medium mb-4 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
            style={{ color: THEME.dark }}
          >
            <div 
              className="w-6 h-6 rounded-full flex items-center justify-center"
              style={{ backgroundColor: THEME.light }}
            >
              <Check className="h-3.5 w-3.5" style={{ color: THEME.dark }} />
            </div>
            {showPurchased ? 'Hide' : 'Show'} purchased ({filteredPurchased.length})
          </button>
          
          {showPurchased && (
            <div className="space-y-2 opacity-70">
              {filteredPurchased.map((item) => (
                <Card 
                  key={item.id} 
                  className="bg-gray-50 border-gray-100"
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <Checkbox
                        checked={true}
                        onCheckedChange={() => handleTogglePurchased(item)}
                        className="h-5 w-5 border-2 data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600"
                      />
                      <span className="line-through text-gray-500 flex-1">{item.name}</span>
                      {item.quantity && (
                        <Badge variant="outline" className="text-xs opacity-50">
                          x{item.quantity}
                        </Badge>
                      )}
                      {!item.favorite_id && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleSaveAsFavorite(item)}
                          className="text-amber-500 hover:text-amber-600 hover:bg-amber-50"
                          title="Save as favorite"
                        >
                          <Star className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      <AddItemDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        listId={list.id}
        onSuccess={() => {
          fetchItems();
          onUpdate();
          setDialogOpen(false);
        }}
      />
    </div>
  );
};

export default ShoppingListView;
